import Close from '@mui/icons-material/Close'
import TableChart from '@mui/icons-material/TableChart'
import BarChart from '@mui/icons-material/BarChart'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState, useMemo } from 'react'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import { MOMENT_FORMATS, REPORT_DATE_TYPES } from 'src/consumer/constants'
import ClinicReportComponent from '../../ClinicOwnerReport/reports/components/ClinicReportComponent'
import {
  fetchClinicBrandReportsForOwner,
  getCorrectReportDateStringForClinicLocationReports,
} from '../../ClinicOwnerReport/reports/utils'
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { getFeeMapString } from '../../ClinicOwnerReport/reports/utils'

const COLORS = [
  '#7450AA',
  '#FF5F35',
  '#34EBB7',
  '#418571',
  '#97271F',
  '#18411F',
  '#FFA500',
  '#9B59B6',
]

export default function ClinicLocationStatsDrawer({
  clinicLocationData,
  dayjsDate = dayjs(),
  open,
  onClose,
}) {
  const [reportData, setReportData] = useState(null)
  const [screeners, setScreeners] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(1) // Default to visualizations tab
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // Lock body scroll when drawer is open
  useBodyScrollLock(open)

  // Fix aria-hidden accessibility issue: Remove aria-hidden from drawer root when drawer paper has focus
  useEffect(() => {
    if (!open) return

    const fixAriaHidden = () => {
      const drawerPaper = document.querySelector('[class*="MuiDrawer-paper"]')
      const drawerRoot = document.querySelector('[class*="MuiDrawer-root"][class*="MuiModal-root"]')
      
      if (drawerPaper && drawerRoot) {
        // Check if drawer paper or any of its children has focus
        const activeElement = document.activeElement
        if (drawerPaper.contains(activeElement)) {
          // Remove aria-hidden from drawer root when drawer has focus
          drawerRoot.removeAttribute('aria-hidden')
        }
      }
    }

    // Fix immediately and on focus events
    fixAriaHidden()
    
    // Listen for focus events within the drawer
    const handleFocus = () => {
      setTimeout(fixAriaHidden, 0)
    }
    
    document.addEventListener('focusin', handleFocus, true)
    
    return () => {
      document.removeEventListener('focusin', handleFocus, true)
    }
  }, [open])

  async function fetchPrevious7DaysReports() {
    const today = dayjs().endOf('day').utc().format(MOMENT_FORMATS.javaInstantFormat)
    const reports = await Promise.all(
      Array.from({ length: 7 }).map(async (_, index) => {
        const endTime = dayjs
          .utc(today)
          .subtract(index, 'days')
          .format(MOMENT_FORMATS.javaInstantFormat)
        const startTime = dayjs
          .utc(today)
          .subtract(index + 1, 'days')
          .format(MOMENT_FORMATS.javaInstantFormat)

        const reports = await fetchClinicBrandReportsForOwner({
          selectedClinicBrandId: clinicLocationData?.clinicBrandId,
          startTime,
          endTime,
          clinicLocationIdsFilter: [clinicLocationData?.id],
        })
        return { startTime, endTime, ...(reports || {}) }
      }),
    )
    return reports
  }

  async function Setup() {
    if (!clinicLocationData) return
    setLoading(true)
    try {
      const reports = await fetchPrevious7DaysReports()
      setReportData(reports)
    } catch (error) {
      console.error('Error fetching reports:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && clinicLocationData) {
      Setup()
    }
  }, [open, clinicLocationData])

  // Process reports for the current clinic location
  const processedReports = useMemo(() => {
    if (!reportData || !clinicLocationData) return []

    return reportData
      .map((report) => {
        const foundReport = report?.reports?.find(
          (r) => r.clinicLocationId === clinicLocationData?.id,
        )
        return {
          ...foundReport,
          reportEndTime: report?.endTime,
          reportStartTime: report?.startTime,
        }
      })
      .filter((r) => r?.metrics)
      .sort((a, b) => dayjs.utc(a?.reportEndTime) - dayjs.utc(b?.reportEndTime))
  }, [reportData, clinicLocationData])

  // Get date label for charts
  const getDateLabel = (reportEndTime, reportStartTime) => {
    const date = dayjs.utc(reportEndTime).local()
    if (date.isSame(dayjs(), 'day')) return 'Today'
    if (date.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday'
    return date.format('MMM DD')
  }

  // Prepare chart data
  const chartData = useMemo(() => {
    return processedReports.map((report) => ({
      date: getDateLabel(report.reportEndTime, report.reportStartTime),
      consultations: report?.metrics?.consultations?.totalBookedCount || 0,
      newConsultations: report?.metrics?.consultations?.newBookedCount || 0,
      repeatConsultations: report?.metrics?.consultations?.repeatBookedCount || 0,
      patientVisits: report?.metrics?.patientVisits?.totalBookedCount || 0,
      newPatientVisits: report?.metrics?.patientVisits?.newBookedCount || 0,
      repeatPatientVisits: report?.metrics?.patientVisits?.repeatBookedCount || 0,
      therapies: report?.metrics?.therapies?.totalBookedCount || 0,
      newTherapies: report?.metrics?.therapies?.newBookedCount || 0,
      existingTherapies: report?.metrics?.therapies?.repeatBookedCount || 0,
      preScreenings: report?.metrics?.preScreenings?.totalBookedCount || 0,
      postScreenings: report?.metrics?.postScreenings?.totalBookedCount || 0,
    }))
  }, [processedReports])

  // Calculate totals
  const totals = useMemo(() => {
    return chartData.reduce(
      (acc, curr) => ({
        consultations: acc.consultations + curr.consultations,
        newConsultations: acc.newConsultations + curr.newConsultations,
        repeatConsultations: acc.repeatConsultations + curr.repeatConsultations,
        patientVisits: acc.patientVisits + curr.patientVisits,
        therapies: acc.therapies + curr.therapies,
        preScreenings: acc.preScreenings + curr.preScreenings,
        postScreenings: acc.postScreenings + curr.postScreenings,
      }),
      {
        consultations: 0,
        newConsultations: 0,
        repeatConsultations: 0,
        patientVisits: 0,
        therapies: 0,
        preScreenings: 0,
        postScreenings: 0,
      },
    )
  }, [chartData])

  // Fee collection data (for potential future use)
  const feeData = useMemo(() => {
    return processedReports
      .map((report) => {
        const feeMap = report?.metrics?.consultations?.currencyToFeeCollectedMap
        if (!feeMap) return null
        return {
          date: getDateLabel(report.reportEndTime, report.reportStartTime),
          feeString: getFeeMapString({ currencyToFeeCollectedMap: feeMap }),
          feeMap,
        }
      })
      .filter(Boolean)
  }, [processedReports])

  const renderTableTab = () => (
    <Box sx={{ p: 3, pb: 10 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <ClinicReportComponent
          clinicLocation={clinicLocationData}
          allReports={reportData}
          reportType={REPORT_DATE_TYPES.day}
          getCorrectReportDateString={(dayJsReportDate) => {
            return getCorrectReportDateStringForClinicLocationReports({
              dayJsReportDate,
              reportType: REPORT_DATE_TYPES.day,
            })
          }}
          allScreeners={screeners}
          accordionOpenAlways={true}
        />
      )}
    </Box>
  )

  const renderVisualizationsTab = () => (
    <Box sx={{ p: 3, pb: 10 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No data available for visualization
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Summary Cards */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #7450AA 0%, #9B59B6 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Consultations
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totals.consultations}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #FF5F35 0%, #FF8C69 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Patient Visits
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totals.patientVisits}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #34EBB7 0%, #418571 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Therapies
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totals.therapies}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #97271F 0%, #C0392B 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Screenings
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totals.preScreenings + totals.postScreenings}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Consultations Chart */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Consultations Overview
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorConsultations" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7450AA" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#7450AA" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF5F35" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#FF5F35" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorRepeat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34EBB7" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#34EBB7" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="consultations"
                    stroke="#7450AA"
                    fillOpacity={1}
                    fill="url(#colorConsultations)"
                    name="Total Consultations"
                  />
                  <Area
                    type="monotone"
                    dataKey="newConsultations"
                    stroke="#FF5F35"
                    fillOpacity={1}
                    fill="url(#colorNew)"
                    name="New Consultations"
                  />
                  <Area
                    type="monotone"
                    dataKey="repeatConsultations"
                    stroke="#34EBB7"
                    fillOpacity={1}
                    fill="url(#colorRepeat)"
                    name="Repeat Consultations"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Patient Visits Chart */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Patient Visits
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="newPatientVisits" fill="#FF5F35" name="New Patients" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="repeatPatientVisits" fill="#34EBB7" name="Repeat Patients" radius={[8, 8, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Therapies and Screenings Comparison */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Therapies Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="therapies"
                        stroke="#34EBB7"
                        strokeWidth={3}
                        dot={{ fill: '#34EBB7', r: 5 }}
                        name="Total Therapies"
                      />
                      <Line
                        type="monotone"
                        dataKey="newTherapies"
                        stroke="#FF5F35"
                        strokeWidth={2}
                        dot={{ fill: '#FF5F35', r: 4 }}
                        name="New Therapies"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Screenings Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Pre Screenings', value: totals.preScreenings },
                          { name: 'Post Screenings', value: totals.postScreenings },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[
                          { name: 'Pre Screenings', value: totals.preScreenings },
                          { name: 'Post Screenings', value: totals.postScreenings },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* New vs Repeat Comparison */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                New vs Repeat Patients Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#666" />
                  <YAxis dataKey="date" type="category" stroke="#666" width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="newPatientVisits" fill="#FF5F35" name="New Patients" radius={[0, 8, 8, 0]} />
                  <Bar dataKey="repeatPatientVisits" fill="#34EBB7" name="Repeat Patients" radius={[0, 8, 8, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 900,
          maxWidth: isMobile ? '100%' : '90vw',
          maxHeight: '100vh',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true, // We handle scroll lock with useBodyScrollLock hook
        disableEnforceFocus: false, // Allow focus management
        disableAutoFocus: false, // Allow auto-focus on open
        hideBackdrop: false,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #7450AA 0%, #9B59B6 100%)',
            color: 'white',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Clinic Statistics
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {clinicLocationData?.nickName || clinicLocationData?.title || 'Location'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 64,
              },
              '& .Mui-selected': {
                color: '#7450AA',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#7450AA',
                height: 3,
              },
            }}
          >
            <Tab
              icon={<TableChart />}
              iconPosition="start"
              label="Table View"
              sx={{ gap: 1 }}
            />
            <Tab
              icon={<BarChart />}
              iconPosition="start"
              label="Visualizations"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: '#f5f5f5',
          }}
        >
          {activeTab === 0 ? renderTableTab() : renderVisualizationsTab()}
        </Box>
      </Box>
    </Drawer>
  )
}

